/* eslint-disable react/no-multi-comp */
import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Button, Collapse, colors, Grid, List, ListItem, Theme, Typography } from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import useRouter from 'utils/useRouter';
import { NavigationListItem } from './components';
import { isAdminRoute } from 'routes';
import { useSessionSelector } from 'reducers/sessionReducer';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(3)
  },
  withChildrenRoot: {
    position: 'relative'
  },
  withChildrenIcon: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%) rotate(180deg)',
    transition: 'all 200ms',
  },
  withChildrenIconOpen: {
    transform: 'translateY(-50%) rotate(0)'
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 'normal',
    '&.depth-0': {
      fontWeight: 500
    },
  }
}));

type Page = {title: string, href: string, icon: React.FC} | {title: string, icon: React.FC, children: Page[]}

interface NavigationListProps {
  pages: Page[],
  depth: number,
  router: ReturnType<typeof useRouter>,
  classes: ReturnType<typeof useStyles>
  open: Record<string, boolean>,
  toggleOpen: (title: string) => void
}

const NavigationList = (props: NavigationListProps) => {
  const { pages, ...rest } = props;

  const { isAdmin } = useSessionSelector().user;

  return (
    <List>
      {pages.reduce(
        (items, page) => reduceChildRoutes({ items, page, isAdmin, ...rest }),
        []
      )}
    </List>
  );
};

NavigationList.propTypes = {
  depth: PropTypes.number,
  pages: PropTypes.array
};

const reduceChildRoutes = props => {
  const { router, items, page, depth, isAdmin, open, toggleOpen, classes } = props;

  if(isAdminRoute(page.href) && !isAdmin) {
    return items;
  }

  if(page.children && !isAdmin && page.children.every((page) => isAdminRoute(page.href))) {
    return items;
  }

  if (page.children) {
    items.push(
      <ListItem>
        <Grid
          container
          direction='column'>
          <Button
            className={clsx(classes.button, `depth-${depth}`)}
            onClick={() => toggleOpen(page.title)}
          >
            <Grid
              alignItems='center'
              className={classes.withChildrenRoot}
              container
              direction='column'
              item>
              <page.icon/>
              {page.title}
            </Grid>
            <ExpandLessIcon
              className={clsx(classes.withChildrenIcon, {
                [classes.withChildrenIconOpen]: !!open[page.title]
              })}
            />
          </Button>
          <Collapse in={!!open[page.title]}>
            <NavigationList
              classes={classes}
              depth={depth + 1}
              open={open}
              pages={page.children}
              router={router}
              toggleOpen={toggleOpen}
            />
          </Collapse>
        </Grid>
      </ListItem>
    );
  } else {
    items.push(
      <NavigationListItem
        depth={depth}
        href={page.href}
        icon={page.icon}
        key={page.title}
        label={page.label}
        title={page.title}
      />
    );
  }

  return items;
};

const Navigation = props => {
  const { title, pages, className, component: Component, ...rest } = props;

  const classes = useStyles();
  const router = useRouter();
  const [open, setOpenState] = React.useState<Record<string, boolean>>({});

  const toggleOpen = (title: string) => setOpenState(pv => ({...pv, [title]: pv[title] != null ? !pv[title]:true}));

  return (
    <Component
      {...rest}
      className={clsx(classes.root, className)}
    >
      {title && <Typography variant="overline">{title}</Typography>}
      <NavigationList
        classes={classes}
        depth={0}
        open={open}
        pages={pages}
        router={router}
        toggleOpen={toggleOpen}
      />
    </Component>
  );
};

Navigation.propTypes = {
  className: PropTypes.string,
  component: PropTypes.any,
  pages: PropTypes.array.isRequired,
  title: PropTypes.string
};

Navigation.defaultProps = {
  component: 'nav'
};

export default Navigation;
